import React, { useState } from 'react';
import axios from 'axios';
import BASE_URL from '../../url';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, AreaChart, Area } from 'recharts';
import '../styles/ManagerDashboard.css';
import {
  FiTrendingUp,
  FiUsers,
  FiAward,
  FiLayers,
  FiX,
  FiBriefcase,
  FiCheckCircle,
  FiAlertTriangle,
  FiUserCheck,
  FiXCircle,
  FiRepeat,
  FiEye,
  FiFileText,
  FiPlayCircle,
  FiCheckSquare
} from 'react-icons/fi';
import MissingSubmissionsModal from './MissingSubmissionsModal';
import PoolToClientModal from './PoolToClientModal';
import SourcingPipelineModal from './SourcingPipelineModal';

// ─────────────────────────────────────────
// Submissions & Sourced Modal with Tab Filtering
// ─────────────────────────────────────────
const SubmissionsModal = ({ recruiter, startDate, endDate, onClose }) => {
  const [applications, setApplications] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'sourced' | 'submitted'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${BASE_URL}/api/recruitment/applications/job-report/submissions`,
          {
            params: { recruiterId: recruiter.id, startDate, endDate },
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        // Map data with pipeline category
        const mapped = response.data.map(role => {
          let loc = role.location || 'N/A';
          if (loc !== 'N/A') {
            loc = loc.split(',').map(p => p.trim()).filter(p => p && p !== '.').join(', ');
          }
          const cat = role.pipelineCategory || (role.isSubmitted ? 'SUBMISSIONS' : 'SOURCED');
          return {
            id: role.id,
            submissionId: role.submissionId,
            candidateName: role.candidateName || 'Candidate',
            candidateEmail: role.candidateEmail || 'N/A',
            candidatePhone: role.candidatePhone || 'N/A',
            stageCode: role.stageCode || 'NEW',
            phaseCode: role.phaseCode || 'INTERNAL',
            currentStep: role.currentStep || 0,
            isSubmitted: cat === 'SUBMISSIONS',
            pipelineCategory: cat,
            jobId: `JOB-${String(role.id).padStart(5, '0')}`,
            externalJobRef: role.externalJobRef || role.jobId || 'N/A',
            clientName: role.clientName || 'N/A',
            jobTitle: role.jobTitle || 'N/A',
            employmentType: role.employmentType || 'Contract',
            experience: role.experience || 'N/A',
            location: loc,
            rate: `${role.currency || 'USD'} ${role.minRate || 0} - ${role.maxRate || 0}`,
            progress: role.currentStep || 0,
            stepCounts: role.stepCounts || '',
            createdAt: role.createdAt,
          };
        });

        setApplications(mapped);
      } catch (err) {
        console.error('Error fetching recruiter applications:', err);
        setError('Failed to load applications.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [recruiter.id, startDate, endDate]);

  const formatTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateRange = () => {
    const s = new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const e = new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${s} — ${e}`;
  };

  const filteredApps = React.useMemo(() => {
    if (activeTab === 'sourced') return applications.filter(a => a.pipelineCategory === 'SOURCED');
    if (activeTab === 'submitted') return applications.filter(a => a.pipelineCategory === 'SUBMISSIONS');
    if (activeTab === 'outcome') return applications.filter(a => a.pipelineCategory === 'OUTCOME');
    return applications;
  }, [applications, activeTab]);

  const sourcedCount = applications.filter(a => a.pipelineCategory === 'SOURCED').length;
  const submittedCount = applications.filter(a => a.pipelineCategory === 'SUBMISSIONS').length;
  const outcomeCount = applications.filter(a => a.pipelineCategory === 'OUTCOME').length;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" style={{ maxWidth: '1100px', width: '95%' }} onClick={ev => ev.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div>
            <h3 className="modal-title">Applications by {recruiter.name}</h3>
            <p className="modal-subtitle">{formatDateRange()}</p>
          </div>
          <button className="modal-close-btn" onClick={onClose}><FiX size={20} /></button>
        </div>

        {/* Tab Filters */}
        <div style={{ display: 'flex', gap: '10px', padding: '12px 24px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
          <button
            onClick={() => setActiveTab('all')}
            style={{
              padding: '6px 14px',
              borderRadius: '6px',
              border: activeTab === 'all' ? '1.5px solid #3b5bfd' : '1px solid #cbd5e1',
              background: activeTab === 'all' ? '#3b5bfd' : '#ffffff',
              color: activeTab === 'all' ? '#ffffff' : '#475569',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            All Applications ({applications.length})
          </button>
          <button
            onClick={() => setActiveTab('sourced')}
            style={{
              padding: '6px 14px',
              borderRadius: '6px',
              border: activeTab === 'sourced' ? '1.5px solid #4f46e5' : '1px solid #cbd5e1',
              background: activeTab === 'sourced' ? '#4f46e5' : '#ffffff',
              color: activeTab === 'sourced' ? '#ffffff' : '#475569',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            👥 Sourced Pre-Client ({sourcedCount})
          </button>
          <button
            onClick={() => setActiveTab('submitted')}
            style={{
              padding: '6px 14px',
              borderRadius: '6px',
              border: activeTab === 'submitted' ? '1.5px solid #3b5bfd' : '1px solid #cbd5e1',
              background: activeTab === 'submitted' ? '#3b5bfd' : '#ffffff',
              color: activeTab === 'submitted' ? '#ffffff' : '#475569',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            🚀 Submitted to Client ({submittedCount})
          </button>
          <button
            onClick={() => setActiveTab('outcome')}
            style={{
              padding: '6px 14px',
              borderRadius: '6px',
              border: activeTab === 'outcome' ? '1.5px solid #dc2626' : '1px solid #cbd5e1',
              background: activeTab === 'outcome' ? '#dc2626' : '#ffffff',
              color: activeTab === 'outcome' ? '#ffffff' : '#475569',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            ⚠️ Rejected / Withdrawn ({outcomeCount})
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {loading ? (
            <div className="modal-loading">
              <div className="modal-spinner" />
              <p>Loading applications...</p>
            </div>
          ) : error ? (
            <div className="modal-error">{error}</div>
          ) : filteredApps.length === 0 ? (
            <div className="modal-empty">No applications found matching the selected filter.</div>
          ) : (
            <div className="modal-table-wrapper">
              <table className="submissions-table" style={{ fontSize: '13px' }}>
                <thead>
                  <tr>
                    <th>Candidate</th>
                    <th>Category</th>
                    <th>Stage</th>
                    <th>Client</th>
                    <th>Job Ref</th>
                    <th>Job Title</th>
                    <th>Exp</th>
                    <th>Location</th>
                    <th>Applied Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApps.map((row, idx) => (
                    <tr key={row.submissionId || `${row.id}-${idx}`}>
                      <td>
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{row.candidateName}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{row.candidateEmail}</div>
                      </td>
                      <td>
                        {row.pipelineCategory === 'SUBMISSIONS' ? (
                          <span style={{
                            background: '#eff6ff',
                            color: '#1d4ed8',
                            padding: '3px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 700,
                            border: '1px solid #bfdbfe',
                            display: 'inline-block'
                          }}>
                            SUBMISSION
                          </span>
                        ) : row.pipelineCategory === 'OUTCOME' ? (
                          <span style={{
                            background: '#fef2f2',
                            color: '#b91c1c',
                            padding: '3px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 700,
                            border: '1px solid #fecaca',
                            display: 'inline-block'
                          }}>
                            {row.stageCode || 'OUTCOME'}
                          </span>
                        ) : (
                          <span style={{
                            background: '#eff6ff',
                            color: '#1d4ed8',
                            padding: '3px 8px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 700,
                            border: '1px solid #bfdbfe',
                            display: 'inline-block'
                          }}>
                            SOURCED
                          </span>
                        )}
                      </td>
                      <td>
                        <span style={{
                          fontSize: '11.5px',
                          color: '#475569',
                          fontWeight: 500,
                          textTransform: 'capitalize'
                        }}>
                          {row.stageCode ? row.stageCode.replace(/_/g, ' ').toLowerCase() : 'New'}
                        </span>
                      </td>
                      <td className="client-name">{row.clientName}</td>
                      <td style={{ fontSize: '12.5px', fontWeight: '500', color: '#3b5bfd' }}>{row.externalJobRef}</td>
                      <td>
                        <Link
                          to={`/submissions/${row.id}`}
                          state={{ jobInfo: row }}
                          className="job-title-link"
                          onClick={onClose}
                        >
                          {row.jobTitle}
                        </Link>
                      </td>
                      <td>{row.experience}</td>
                      <td className="na-text" style={{ maxWidth: '130px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.location}>
                        {row.location}
                      </td>
                      <td>{formatTime(row.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && !error && (
          <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', color: '#64748b' }}>
              Showing <strong>{filteredApps.length}</strong> of <strong>{applications.length}</strong> application{applications.length !== 1 ? 's' : ''} ({sourcedCount} Sourced, {submittedCount} Submitted{outcomeCount > 0 ? `, ${outcomeCount} Outcome` : ''})
            </span>
            <button className="modal-close-footer-btn" onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────
const CLIENT_WORKFLOW_STAGES = [
  { key: 'clientReview', code: 'CLIENT_REVIEW', label: 'Client Review', subtitle: 'Reviewing', color: '#4338ca', bg: '#e0e7ff', subColor: '#6366f1', icon: <FiEye /> },
  { key: 'clientInterview1', code: 'CLIENT_INTERVIEW_1', label: 'Client Int 1', subtitle: 'Round 1', color: '#2563eb', bg: '#dbeafe', subColor: '#3b82f6', icon: <FiUserCheck /> },
  { key: 'clientInterview2', code: 'CLIENT_INTERVIEW_2', label: 'Client Int 2', subtitle: 'Round 2', color: '#7c3aed', bg: '#ede9fe', subColor: '#8b5cf6', icon: <FiUsers /> },
  { key: 'clientFinalRound', code: 'CLIENT_FINAL_ROUND', label: 'Final Round', subtitle: 'Decision', color: '#d97706', bg: '#fef3c7', subColor: '#f59e0b', icon: <FiAward /> },
  { key: 'offer', code: 'OFFER', label: 'Offer', subtitle: 'Extended', color: '#e11d48', bg: '#ffe4e6', subColor: '#f43f5e', icon: <FiFileText /> },
  { key: 'onboardingInitiated', code: 'ONBOARDING_INITIATED', label: 'Onboarding Init', subtitle: 'Checklist', color: '#0891b2', bg: '#cffafe', subColor: '#06b6d4', icon: <FiPlayCircle /> },
  { key: 'onboardingCompleted', code: 'ONBOARDING_COMPLETED', label: 'Onboard Done', subtitle: 'Verified', color: '#059669', bg: '#d1fae5', subColor: '#10b981', icon: <FiCheckSquare /> },
  { key: 'hired', code: 'HIRED', label: 'Hired', subtitle: 'Successful', color: '#16a34a', bg: '#dcfce7', subColor: '#22c55e', icon: <FiCheckCircle /> }
];
const POOL_CLIENT_STAGES = CLIENT_WORKFLOW_STAGES;

// ─────────────────────────────────────────
// Main ManagerDashboard
// ─────────────────────────────────────────
const ManagerDashboard = ({ reportData, trendData, jobStats, activeFilter, onFilterChange, isLoading, startDate, endDate, region, client }) => {
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [highlightedRecruiter, setHighlightedRecruiter] = useState(null);
  const [highlightedTotalRecruiter, setHighlightedTotalRecruiter] = useState(null);
  const [isMissingModalOpen, setIsMissingModalOpen] = useState(false);
  const [isPoolModalOpen, setIsPoolModalOpen] = useState(false);
  const [poolStageFilter, setPoolStageFilter] = useState('ALL');
  const [isPipelineModalOpen, setIsPipelineModalOpen] = useState(false);
  const [pipelineFilterKey, setPipelineFilterKey] = useState('ALL');

  const handleLegendClick = (recruiterName) => {
    setHighlightedRecruiter(prev => prev === recruiterName ? null : recruiterName);
  };

  const handleTotalLegendClick = (recruiterName) => {
    setHighlightedTotalRecruiter(prev => prev === recruiterName ? null : recruiterName);
  };

  // Compute KPIs from reportData (both Submissions, Sourced, and Outcome)
  let totalSubmissions = 0;
  let totalSourced = 0;
  let totalOutcome = 0;
  const uniqueMembers = new Set();

  if (reportData && reportData.length > 0) {
    reportData.forEach(team => {
      if (team.members) {
        team.members.forEach(m => {
          if (!uniqueMembers.has(m.id)) {
            uniqueMembers.add(m.id);
            totalSubmissions += (m.submissionCount || 0);
            totalSourced += (m.sourcedCount || 0);
            totalOutcome += (m.outcomeCount || 0);
          }
        });
      }
    });
  }

  // Direct Submissions stage distribution
  const directStageTotals = jobStats?.directStageTotals || {
    clientReview: 0,
    clientInterview1: 0,
    clientInterview2: 0,
    clientFinalRound: 0,
    offer: 0,
    onboardingInitiated: 0,
    onboardingCompleted: 0,
    hired: 0
  };
  const directStageSum = Object.values(directStageTotals).reduce((a, b) => a + b, 0);
  const directSubsDisplay = totalSubmissions > 0 ? totalSubmissions : directStageSum;
  const directStageAdjusted = { ...directStageTotals };
  if (directSubsDisplay > directStageSum) {
    directStageAdjusted.clientReview += (directSubsDisplay - directStageSum);
  }

  // Find Top Recruiter
  let topRecruiter = { name: '-', count: 0, sourced: 0, outcome: 0 };
  let allMembers = [];
  if (reportData && reportData.length > 0) {
    reportData.forEach(team => {
      if (team.members) {
        team.members.forEach(member => {
          const subCount = member.submissionCount || 0;
          const srcCount = member.sourcedCount || 0;
          const outCount = member.outcomeCount || 0;
          allMembers.push({
            id: member.id,
            name: member.name,
            submissions: subCount,
            sourced: srcCount,
            outcome: outCount,
            total: subCount + srcCount + outCount,
            team: team.teamName
          });
          if (subCount > topRecruiter.count) {
            topRecruiter = { name: member.name, count: subCount, sourced: srcCount, outcome: outCount };
          }
        });
      }
    });
  }

  // Sort members by submissions for the chart
  const sortedMembers = [...allMembers].sort((a, b) => b.submissions - a.submissions || b.sourced - a.sourced);

  // Find Top Pod
  let topPod = { name: '-', count: 0 };
  if (reportData && reportData.length > 0) {
    reportData.forEach(team => {
      if ((team.totalSubmissions || 0) > topPod.count) {
        topPod = { name: team.teamName, count: team.totalSubmissions || 0 };
      }
    });
  }

  // Helper to format recruiter display name in breakdown table (15 characters max + ...)
  const formatRecruiterDisplayName = (name) => {
    if (!name) return '';
    if (name.toLowerCase().includes('abinesh anbazhagan')) {
      return 'Abinesh....';
    }
    if (name.length > 15) {
      return name.substring(0, 15).trimEnd() + '...';
    }
    return name;
  };

  // Helper to format team name in breakdown table (15 characters max + ...)
  const formatTeamName = (team) => {
    if (!team) return '';
    const trimmed = team.trim();
    if (trimmed.toLowerCase().includes('abinesh anbazhagan')) {
      return 'Abinesh ..... Team';
    }
    const parts = trimmed.split(/\s+/);
    if (parts.length >= 3 && parts[parts.length - 1].toLowerCase() === 'team') {
      return `${parts[0]} ..... Team`;
    }
    if (trimmed.length > 15) {
      return trimmed.substring(0, 15).trimEnd() + '...';
    }
    return team;
  };

  // Helper to get ISO week number
  const getWeekNumber = (d) => {
    const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    const dayNum = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  };

  // Process daily trend data for grouped bar chart
  const trendMap = {};
  const allRecruitersInTrends = new Set();
  const totalTrendMap = {};
  const allRecruitersInTotalTrends = new Set();

  if (trendData && trendData.length > 0) {
    trendData.forEach(item => {
      const d = new Date(item.date);
      let groupKey = item.date;
      let displayStr = d.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });
      let sortDate = item.date;

      if (activeFilter === 'weekly') {
        const weekNum = getWeekNumber(d);
        const day = d.getUTCDay();
        const diff = d.getUTCDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), diff));

        groupKey = `W${weekNum}-${monday.getUTCFullYear()}`;
        displayStr = `W${weekNum} (${monday.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' })})`;
        sortDate = monday.toISOString().split('T')[0];
      } else if (activeFilter === 'monthly') {
        groupKey = `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
        displayStr = d.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', year: 'numeric' });
        sortDate = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString().split('T')[0];
      } else if (activeFilter === 'yearly') {
        groupKey = `${d.getUTCFullYear()}`;
        displayStr = `${d.getUTCFullYear()}`;
        sortDate = new Date(Date.UTC(d.getUTCFullYear(), 0, 1)).toISOString().split('T')[0];
      }

      if (!trendMap[groupKey]) {
        trendMap[groupKey] = { dateStr: displayStr, fullDate: sortDate, totalSourced: 0, totalSubmissions: 0 };
      }
      if (!totalTrendMap[groupKey]) {
        totalTrendMap[groupKey] = { dateStr: displayStr, fullDate: sortDate, total: 0 };
      }

      const subs = item.submissions || 0;
      const src = item.sourced || 0;
      const tot = (item.total !== undefined ? item.total : (subs + src)) || (subs + src);

      if (subs > 0) {
        trendMap[groupKey][item.recruiterName] = (trendMap[groupKey][item.recruiterName] || 0) + subs;
        allRecruitersInTrends.add(item.recruiterName);
      }
      if (tot > 0) {
        totalTrendMap[groupKey][item.recruiterName] = (totalTrendMap[groupKey][item.recruiterName] || 0) + tot;
        allRecruitersInTotalTrends.add(item.recruiterName);
      }
      trendMap[groupKey].totalSubmissions += subs;
      trendMap[groupKey].totalPreClient = (trendMap[groupKey].totalPreClient || 0) + src;
      trendMap[groupKey].totalSourced += (src + subs);
      totalTrendMap[groupKey].total += tot;
    });
  }

  const dailyTrendChartData = Object.values(trendMap).sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));
  const trendRecruiters = Array.from(allRecruitersInTrends);

  const totalTrendChartData = Object.values(totalTrendMap).sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));
  const totalTrendRecruiters = Array.from(allRecruitersInTotalTrends);
  const COLORS = ['#3b82f6', '#f43f5e', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#84cc16', '#ec4899', '#6366f1', '#14b8a6', '#64748b'];

  return (
    <div className="manager-dashboard">
      {/* Modal for recruiter submissions */}
      {selectedRecruiter && (
        <SubmissionsModal
          recruiter={selectedRecruiter}
          startDate={startDate}
          endDate={endDate}
          onClose={() => setSelectedRecruiter(null)}
        />
      )}

      {/* Modal for jobs missing submissions */}
      {isMissingModalOpen && (
        <MissingSubmissionsModal
          startDate={startDate}
          endDate={endDate}
          region={region}
          client={client}
          onClose={() => setIsMissingModalOpen(false)}
        />
      )}

      {/* Modal for pool to client candidates */}
      {isPoolModalOpen && (
        <PoolToClientModal
          startDate={startDate}
          endDate={endDate}
          region={region}
          client={client}
          initialStageFilter={poolStageFilter}
          onClose={() => {
            setIsPoolModalOpen(false);
            setPoolStageFilter('ALL');
          }}
        />
      )}

      {/* Modal for sourcing & direct submissions pipeline */}
      {isPipelineModalOpen && (
        <SourcingPipelineModal
          startDate={startDate}
          endDate={endDate}
          region={region}
          client={client}
          initialFilter={pipelineFilterKey}
          onClose={() => {
            setIsPipelineModalOpen(false);
            setPipelineFilterKey('ALL');
          }}
        />
      )}

      {/* Header & Filters */}
      <div className="dashboard-header">
        <div>
          <h2>Manager Dashboard</h2>
          <p className="subtitle">Submission analytics by recruiter & job coverage</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div className="filter-buttons">
            {['daily', 'weekly', 'monthly', 'yearly'].map(f => (
              <button
                key={f}
                className={`filter-btn ${activeFilter === f ? 'active' : ''}`}
                onClick={() => onFilterChange(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ROW 1: SOURCING & DIRECT CLIENT SUBMISSIONS PIPELINE (IN SAME FIRST ROW) */}
      <div className="sourcing-pipeline-row">
        <div className="pool-row-header">
          <span className="pipeline-row-badge direct-badge">
            <FiTrendingUp /> SOURCING &amp; DIRECT SUBMISSIONS PIPELINE
          </span>
          <span className="pool-row-subtitle">
            Internal Pipeline (<strong>{totalSourced + totalSubmissions + totalOutcome} Sourced</strong>) &bull; Direct Submissions (<strong>{directSubsDisplay} Total</strong>)
          </span>
        </div>

        <div className="row1-pipeline-container">
          {/* Pre-Client / Internal Funnel (3 Cards) */}
          <div className="preclient-cards-strip">
            {/* 1. SOURCED CARD (TOTAL SOURCED) */}
            <div
              className="kpi-card"
              style={{ borderLeft: '2px solid #6366f1', background: '#ffffff', cursor: 'pointer', transition: 'all 0.15s ease' }}
              onClick={() => {
                setPipelineFilterKey('ALL');
                setIsPipelineModalOpen(true);
              }}
              title="Click to view all sourced candidates in table view"
            >
              <div className="kpi-header">
                <span style={{ color: '#4338ca', fontWeight: 700 }}>SOURCED</span>
                <div className="icon-wrapper" style={{ background: '#e0e7ff', color: '#4f46e5' }}>
                  <FiUserCheck />
                </div>
              </div>
              <div className="kpi-value" style={{ color: '#3730a3' }}>{totalSourced + totalSubmissions + totalOutcome}</div>
              <div className="kpi-footer" style={{ color: '#6366f1', fontWeight: 600 }}>Total Sourced</div>
            </div>

            {/* 2. INTERNALLY HOLD / REJECTED / WITHDRAWN CARD */}
            <div
              className="kpi-card"
              style={{ borderLeft: '2px solid #ef4444', background: '#ffffff', cursor: 'pointer', transition: 'all 0.15s ease' }}
              onClick={() => {
                setPipelineFilterKey('OUTCOME');
                setIsPipelineModalOpen(true);
              }}
              title="Click to view candidates on Hold, Internally Rejected, or Withdrawn"
            >
              <div className="kpi-header">
                <span style={{ color: '#b91c1c', fontWeight: 700 }} title="On Hold, Internally Rejected and Withdrawn Submissions">HOLD / REJ / WITHDRAWN</span>
                <div className="icon-wrapper" style={{ background: '#fee2e2', color: '#dc2626' }}>
                  <FiXCircle />
                </div>
              </div>
              <div className="kpi-value" style={{ color: '#dc2626' }}>{totalOutcome}</div>
              <div className="kpi-footer" style={{ color: '#b91c1c', fontWeight: 600 }}>Hold &amp; Out</div>
            </div>

            {/* 3. PRE-CLIENT POOL CARD */}
            <div
              className="kpi-card"
              style={{ borderLeft: '2px solid #8b5cf6', background: '#ffffff', cursor: 'pointer', transition: 'all 0.15s ease' }}
              onClick={() => {
                setPipelineFilterKey('SOURCED');
                setIsPipelineModalOpen(true);
              }}
              title="Click to view pre-client pipeline candidates"
            >
              <div className="kpi-header">
                <span style={{ color: '#6d28d9', fontWeight: 700 }}>PRE-CLIENT POOL</span>
                <div className="icon-wrapper" style={{ background: '#ede9fe', color: '#7c3aed' }}>
                  <FiLayers />
                </div>
              </div>
              <div className="kpi-value" style={{ color: '#5b21b6' }}>{totalSourced}</div>
              <div className="kpi-footer" style={{ color: '#7c3aed', fontWeight: 600 }}>Internal Pipeline</div>
            </div>
          </div>

          {/* Direct Submissions Strip: 1 Total Card + 8 Stage Split Cards (9 Cards) */}
          <div className="direct-cards-strip">
            {/* 4. DIRECT SUBMISSIONS PARENT CARD */}
            <div
              className="kpi-card pool-parent-card"
              style={{ borderLeft: '2px solid #3b5bfd', background: '#ffffff', cursor: 'pointer', transition: 'all 0.15s ease' }}
              onClick={() => {
                setPipelineFilterKey('SUBMISSIONS');
                setIsPipelineModalOpen(true);
              }}
              title="Click to view all active client submissions in table view"
            >
              <div className="kpi-header">
                <span style={{ color: '#3b5bfd', fontWeight: 700 }}>SUBMISSIONS</span>
                <div className="icon-wrapper" style={{ background: '#eff6ff', color: '#3b5bfd' }}>
                  <FiTrendingUp />
                </div>
              </div>
              <div className="kpi-value" style={{ color: '#3b5bfd' }}>{directSubsDisplay}</div>
              <div className="kpi-footer" style={{ color: '#3b5bfd', fontWeight: 600 }}>Active Submissions</div>
            </div>

            {/* 8 Direct Stage Split Cards */}
            {CLIENT_WORKFLOW_STAGES.map(stage => {
              const stageVal = directStageAdjusted[stage.key] ?? 0;
              return (
                <div
                  key={stage.key}
                  className="kpi-card pool-stage-card"
                  style={{
                    borderLeft: `2px solid ${stage.color}`,
                    background: '#ffffff',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                  onClick={() => {
                    setPipelineFilterKey(stage.code);
                    setIsPipelineModalOpen(true);
                  }}
                  title={`Click to view direct submissions in ${stage.label}`}
                >
                  <div className="kpi-header">
                    <span style={{ color: stage.color, fontWeight: 700 }}>{stage.label}</span>
                    <div className="icon-wrapper" style={{ background: stage.bg, color: stage.color }}>
                      {stage.icon}
                    </div>
                  </div>
                  <div className="kpi-value" style={{ color: stage.color }}>{stageVal}</div>
                  <div className="kpi-footer" style={{ color: stage.subColor, fontWeight: 600 }}>{stage.subtitle}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ROW 2: POOL TO CLIENT — DEDICATED INDIVIDUAL ROW SPLITAGE */}
      <div className="pool-to-client-pipeline-row">
        <div className="pool-row-header">
          <span className="pool-row-badge">
            <FiRepeat /> POOL TO CLIENT PIPELINE
          </span>
          <span className="pool-row-subtitle">
            Sourced prior to period &bull; Advanced to client stages during this period (<strong>{jobStats?.poolToClientCount ?? 0} Total</strong>)
          </span>
        </div>

        <div className="pool-stage-grid">
          {/* Parent: POOL TO CLIENT Card */}
          <div
            className="kpi-card pool-parent-card"
            onClick={() => {
              setPoolStageFilter('ALL');
              setIsPoolModalOpen(true);
            }}
            style={{ cursor: 'pointer', borderLeft: '2px solid #06b6d4', background: '#ffffff', transition: 'all 0.15s ease' }}
            title="Click to view all candidates moved from prior Pre-Client pool to Client this period"
          >
            <div className="kpi-header">
              <span style={{ color: '#0891b2', fontWeight: 700 }} title="Sourced prior to period, submitted to client during period">POOL TO CLIENT</span>
              <div className="icon-wrapper" style={{ background: '#cffafe', color: '#0891b2' }}>
                <FiRepeat />
              </div>
            </div>
            <div className="kpi-value" style={{ color: '#0e7490' }}>{jobStats?.poolToClientCount ?? 0}</div>
            <div className="kpi-footer" style={{ color: '#0891b2', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600 }}>
              <span>From Prior Pool</span>
              <span style={{ fontSize: '11px', textDecoration: 'underline' }}>View ↗</span>
            </div>
          </div>

          {/* 8 Stage Split Cards */}
          {POOL_CLIENT_STAGES.map(stage => {
            const stageVal = jobStats?.poolToClientStages?.[stage.key] ?? 0;
            return (
              <div
                key={stage.key}
                className="kpi-card pool-stage-card"
                style={{
                  borderLeft: `2px solid ${stage.color}`,
                  background: '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onClick={() => {
                  setPoolStageFilter(stage.code);
                  setIsPoolModalOpen(true);
                }}
                title={`Click to view candidates in ${stage.label}`}
              >
                <div className="kpi-header">
                  <span style={{ color: stage.color, fontWeight: 700 }}>{stage.label}</span>
                  <div className="icon-wrapper" style={{ background: stage.bg, color: stage.color }}>
                    {stage.icon}
                  </div>
                </div>
                <div className="kpi-value" style={{ color: stage.color }}>{stageVal}</div>
                <div className="kpi-footer" style={{ color: stage.subColor, fontWeight: 600 }}>{stage.subtitle}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ROW 3: JOB COVERAGE & TEAM PERFORMANCE */}
      <div className="manager-kpi-grid job-kpi-grid">

        {/* 3. TOTAL OPEN JOBS */}
        <div className="kpi-card">
          <div className="kpi-header">
            <span>TOTAL OPEN JOBS</span>
            <div className="icon-wrapper teal"><FiBriefcase /></div>
          </div>
          <div className="kpi-value">{jobStats?.totalJobs ?? 0}</div>
          <div className="kpi-footer">In Selected Period</div>
        </div>

        {/* 4. JOBS WITH SUBS */}
        <div className="kpi-card">
          <div className="kpi-header">
            <span>JOBS WITH SUBS</span>
            <div className="icon-wrapper blue"><FiCheckCircle /></div>
          </div>
          <div className="kpi-value" style={{ color: '#2563eb' }}>{jobStats?.jobsWithSubs ?? 0}</div>
          <div className="kpi-footer" style={{ color: '#2563eb', fontWeight: 600 }}>
            {jobStats?.totalJobs > 0 ? `${Math.round(((jobStats.jobsWithSubs || 0) / jobStats.totalJobs) * 100)}% Coverage` : 'Active Submissions'}
          </div>
        </div>

        {/* 5. MISSING SUBS */}
        <div
          className="kpi-card"
          onClick={() => setIsMissingModalOpen(true)}
          style={{ cursor: 'pointer', border: '1px solid #fecaca', background: '#fff5f5', transition: 'all 0.15s ease' }}
          title="Click to view all jobs missing candidate submissions"
        >
          <div className="kpi-header">
            <span style={{ color: '#991b1b', fontWeight: 700 }}>⚠️ MISSING SUBS</span>
            <div className="icon-wrapper orange" style={{ background: '#fee2e2', color: '#dc2626' }}><FiAlertTriangle /></div>
          </div>
          <div className="kpi-value" style={{ color: '#dc2626' }}>{jobStats?.jobsWithoutSubs ?? 0}</div>
          <div className="kpi-footer" style={{ color: '#b91c1c', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600 }}>
            <span>0 Submissions</span>
            <span style={{ fontSize: '11px', textDecoration: 'underline' }}>View Jobs ↗</span>
          </div>
        </div>

        {/* 6. TOP RECRUITER */}
        <div className="kpi-card">
          <div className="kpi-header">
            <span>TOP RECRUITER</span>
            <div className="icon-wrapper orange"><FiAward /></div>
          </div>
          <div className="kpi-value" style={{ fontSize: '17px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={topRecruiter.name}>
            {topRecruiter.name}
          </div>
          <div className="kpi-footer orange-text">{topRecruiter.count} submissions</div>
        </div>

        {/* 7. TOP POD */}
        <div className="kpi-card">
          <div className="kpi-header">
            <span>TOP POD</span>
            <div className="icon-wrapper teal"><FiLayers /></div>
          </div>
          <div className="kpi-value" style={{ fontSize: '17px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={topPod.name}>
            {topPod.name}
          </div>
          <div className="kpi-footer teal-text">{topPod.count} submissions</div>
        </div>
      </div>

      {/* Client Submissions Trend Chart Section */}
      <div className="trend-section">
        <div
          className="trend-header"
          style={{
            backgroundColor: '#f8fafc',
            color: '#1e293b',
            borderBottom: '1px solid #e2e8f0',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              borderRadius: '6px',
              background: '#e0e7ff',
              color: '#4f46e5'
            }}
          >
            <FiTrendingUp size={16} />
          </span>
          <span style={{ fontWeight: 600, fontSize: '15px' }}>
            Client Submissions by Recruiter — {activeFilter && activeFilter !== 'custom' ? activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1) : 'Daily'} View
          </span>
        </div>
        <div className="trend-container">
          {isLoading ? (
            <div className="loading-state">Loading data...</div>
          ) : dailyTrendChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={dailyTrendChartData} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis
                  dataKey="dateStr"
                  axisLine={{ stroke: '#cbd5e1' }}
                  tickLine={false}
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  dx={-10}
                  allowDecimals={false}
                />
                <Tooltip
                  cursor={{ fill: '#f8fafc' }}
                  wrapperStyle={{ zIndex: 9999 }}
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                />
                <Legend
                  iconType="circle"
                  wrapperStyle={{ paddingTop: '20px' }}
                  content={() => (
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px', paddingTop: '16px' }}>
                      {trendRecruiters.map((recruiter, idx) => {
                        const isActive = !highlightedRecruiter || highlightedRecruiter === recruiter;
                        return (
                          <div
                            key={recruiter}
                            onClick={() => handleLegendClick(recruiter)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              cursor: 'pointer',
                              padding: '4px 10px',
                              borderRadius: '20px',
                              background: highlightedRecruiter === recruiter ? COLORS[idx % COLORS.length] + '18' : 'transparent',
                              border: highlightedRecruiter === recruiter ? `1.5px solid ${COLORS[idx % COLORS.length]}` : '1.5px solid transparent',
                              opacity: isActive ? 1 : 0.4,
                              transition: 'all 0.2s ease',
                              fontWeight: highlightedRecruiter === recruiter ? 700 : 400,
                              fontSize: '13px',
                              color: '#475569',
                              userSelect: 'none'
                            }}
                          >
                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: COLORS[idx % COLORS.length], flexShrink: 0 }} />
                            {recruiter}
                          </div>
                        );
                      })}
                      {highlightedRecruiter && (
                        <div
                          onClick={() => setHighlightedRecruiter(null)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '4px',
                            cursor: 'pointer', padding: '4px 10px', borderRadius: '20px',
                            background: '#f1f5f9', fontSize: '12px', color: '#64748b',
                            border: '1px solid #e2e8f0', transition: 'all 0.2s ease'
                          }}
                        >
                          ✕ Clear filter
                        </div>
                      )}
                    </div>
                  )}
                />
                {(highlightedRecruiter ? trendRecruiters.filter(r => r === highlightedRecruiter) : trendRecruiters).map((recruiter) => {
                  const idx = trendRecruiters.indexOf(recruiter);
                  return (
                    <Bar
                      key={recruiter}
                      dataKey={recruiter}
                      fill={COLORS[idx % COLORS.length]}
                      radius={[2, 2, 0, 0]}
                      barSize={6}
                      label={
                        highlightedRecruiter
                          ? { position: 'top', fill: COLORS[idx % COLORS.length], fontSize: 12, fontWeight: 600, dy: -5 }
                          : false
                      }
                    />
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="no-data-chart">No trend data available for the selected period.</div>
          )}
        </div>
      </div>

      {/* Total Sourced Trend Chart Section */}
      <div className="trend-section">
        <div
          className="trend-header"
          style={{
            backgroundColor: '#f8fafc',
            color: '#1e293b',
            borderBottom: '1px solid #e2e8f0',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              borderRadius: '6px',
              background: '#e0e7ff',
              color: '#4f46e5'
            }}
          >
            <FiUsers size={16} />
          </span>
          <span style={{ fontWeight: 600, fontSize: '15px' }}>
            Total Sourced by Recruiter — {activeFilter && activeFilter !== 'custom' ? activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1) : 'Daily'} View
          </span>
        </div>
        <div className="trend-container">
          {isLoading ? (
            <div className="loading-state">Loading data...</div>
          ) : totalTrendChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={totalTrendChartData} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis
                  dataKey="dateStr"
                  axisLine={{ stroke: '#cbd5e1' }}
                  tickLine={false}
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#64748b', fontSize: 12 }}
                  dx={-10}
                  allowDecimals={false}
                />
                <Tooltip
                  cursor={{ fill: '#f8fafc' }}
                  wrapperStyle={{ zIndex: 9999 }}
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                />
                <Legend
                  iconType="circle"
                  wrapperStyle={{ paddingTop: '20px' }}
                  content={() => (
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px', paddingTop: '16px' }}>
                      {totalTrendRecruiters.map((recruiter, idx) => {
                        const isActive = !highlightedTotalRecruiter || highlightedTotalRecruiter === recruiter;
                        return (
                          <div
                            key={recruiter}
                            onClick={() => handleTotalLegendClick(recruiter)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              cursor: 'pointer',
                              padding: '4px 10px',
                              borderRadius: '20px',
                              background: highlightedTotalRecruiter === recruiter ? COLORS[idx % COLORS.length] + '18' : 'transparent',
                              border: highlightedTotalRecruiter === recruiter ? `1.5px solid ${COLORS[idx % COLORS.length]}` : '1.5px solid transparent',
                              opacity: isActive ? 1 : 0.4,
                              transition: 'all 0.2s ease',
                              fontWeight: highlightedTotalRecruiter === recruiter ? 700 : 400,
                              fontSize: '13px',
                              color: '#475569',
                              userSelect: 'none'
                            }}
                          >
                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: COLORS[idx % COLORS.length], flexShrink: 0 }} />
                            {recruiter}
                          </div>
                        );
                      })}
                      {highlightedTotalRecruiter && (
                        <div
                          onClick={() => setHighlightedTotalRecruiter(null)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '4px',
                            cursor: 'pointer', padding: '4px 10px', borderRadius: '20px',
                            background: '#f1f5f9', fontSize: '12px', color: '#64748b',
                            border: '1px solid #e2e8f0', transition: 'all 0.2s ease'
                          }}
                        >
                          ✕ Clear filter
                        </div>
                      )}
                    </div>
                  )}
                />
                {(highlightedTotalRecruiter ? totalTrendRecruiters.filter(r => r === highlightedTotalRecruiter) : totalTrendRecruiters).map((recruiter) => {
                  const idx = totalTrendRecruiters.indexOf(recruiter);
                  return (
                    <Bar
                      key={recruiter}
                      dataKey={recruiter}
                      fill={COLORS[idx % COLORS.length]}
                      radius={[2, 2, 0, 0]}
                      barSize={6}
                      label={
                        highlightedTotalRecruiter
                          ? { position: 'top', fill: COLORS[idx % COLORS.length], fontSize: 12, fontWeight: 600, dy: -5 }
                          : false
                      }
                    />
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="no-data-chart">No trend data available for the selected period.</div>
          )}
        </div>
      </div>

      {/* Total Submission Trend + Detailed Table */}
      <div className="dashboard-bottom-grid">
        {/* Total Submission Trend Line Chart */}
        <div className="chart-section total-trend-section">
          <div className="chart-header total-trend-header">
            <FiTrendingUp className="chart-icon" /> Total Pipeline Activity Trend
          </div>
          <div className="chart-container">
            {isLoading ? (
              <div className="loading-state">Loading data...</div>
            ) : dailyTrendChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart
                  data={dailyTrendChartData.map(d => {
                    const subs = d.totalSubmissions || trendRecruiters.reduce((sum, r) => sum + (d[r] || 0), 0);
                    const sourced = (d.totalSourced !== undefined && d.totalSubmissions !== undefined && d.totalSourced >= subs)
                      ? d.totalSourced
                      : ((d.totalSourced || 0) + subs);
                    return {
                      ...d,
                      totalSubs: subs,
                      totalSourced: sourced
                    };
                  })}
                  margin={{ top: 30, right: 30, left: 0, bottom: 20 }}
                >
                  <defs>
                    <linearGradient id="totalTrendGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b5bfd" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#3b5bfd" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="sourcedTrendGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis
                    dataKey="dateStr"
                    axisLine={{ stroke: '#e2e8f0' }}
                    tickLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 12 }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#94a3b8', fontSize: 12 }}
                    dx={-10}
                    allowDecimals={false}
                    domain={[0, dataMax => dataMax + 3]}
                  />
                  <Tooltip
                    wrapperStyle={{ zIndex: 9999 }}
                    contentStyle={{ borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: '0 4px 16px rgba(0,0,0,0.08)', fontSize: 13 }}
                    labelStyle={{ color: '#1e293b', fontWeight: 600, marginBottom: '4px' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="totalSubs"
                    name="Submissions"
                    stroke="#3b5bfd"
                    strokeWidth={2.5}
                    fill="url(#totalTrendGradient)"
                    dot={{ r: 4, fill: '#3b5bfd', strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 6, fill: '#3b5bfd', stroke: '#fff', strokeWidth: 2 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="totalSourced"
                    name="Sourced"
                    stroke="#6366f1"
                    strokeWidth={2}
                    strokeDasharray="4 4"
                    fill="url(#sourcedTrendGradient)"
                    dot={{ r: 3, fill: '#6366f1', strokeWidth: 1.5, stroke: '#fff' }}
                  />
                  <Legend verticalAlign="top" height={36} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data-chart">No trend data available for the selected period.</div>
            )}
          </div>
        </div>

        {/* Detailed Table */}
        <div className="table-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0 }}>Detailed Breakdown</h3>
            <span style={{ fontSize: '12px', color: '#64748b' }}>
              {allMembers.length} Recruiter{allMembers.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="table-container">
            <table className="breakdown-table">
              <thead>
                <tr>
                  <th>Recruiter</th>
                  <th>Team Pod</th>
                  <th style={{ textAlign: 'center' }} title="Internal Pre-Client Pool">Pre-Client Pool</th>
                  <th style={{ textAlign: 'center' }} title="Client Submissions">Client Submissions</th>
                  <th style={{ textAlign: 'center' }} title="Internally Rejected or Withdrawn">Rejected / Withdrawn</th>
                  <th style={{ textAlign: 'center' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {sortedMembers.map((member, index) => {
                  const isBench = Boolean(
                    (member.team && member.team.toLowerCase().includes('bench')) ||
                    (member.name && ['abinesh anbazhagan', 'pragadeeshwaran karthikeyan', 'karunakaran palani', 'rohinth chelladurai'].some(b => member.name.toLowerCase().includes(b)))
                  );
                  const isAbinesh = Boolean(member.name && member.name.toLowerCase().includes('abinesh anbazhagan'));
                  return (
                    <tr key={index}>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                          <button
                            className="recruiter-link-btn"
                            onClick={() => setSelectedRecruiter({ id: member.id, name: member.name })}
                            title={`View applications by ${member.name}${isBench ? ' (Bench)' : ''}`}
                          >
                            {formatRecruiterDisplayName(member.name)}
                          </button>
                          {isBench && !isAbinesh && (
                            <span style={{
                              background: '#ffedd5',
                              color: '#c2410c',
                              padding: '1px 6px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: 700,
                              border: '1px solid #fed7aa',
                              whiteSpace: 'nowrap'
                            }}>
                              Bench
                            </span>
                          )}
                        </span>
                      </td>
                      <td title={member.team} style={{ whiteSpace: 'nowrap' }}>
                        {formatTeamName(member.team)}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{
                          background: '#eff6ff',
                          color: '#1d4ed8',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 700,
                          border: '1px solid #bfdbfe',
                          display: 'inline-block'
                        }}>
                          {member.sourced}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className="submission-badge">{member.submissions}</span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{
                          background: '#fef2f2',
                          color: '#b91c1c',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 700,
                          border: '1px solid #fecaca',
                          display: 'inline-block'
                        }}>
                          {member.outcome}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 600, color: '#334155' }}>
                        {member.total}
                      </td>
                    </tr>
                  );
                })}
                {sortedMembers.length === 0 && (
                  <tr>
                    <td colSpan="6" className="no-data-table">No data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagerDashboard;
